import { injectable } from 'inversify';
import container from '@core/di';
import BaseStore from '@core/stores/base';
import { action, makeObservable, observable, runInAction } from 'mobx';
import AssessmentsService, { GetPendingAssessmentsParams } from '@shared/services/assessments';
import {
  BrainFunctionContent,
  BrainFunctionDeleteData,
  BrainFunctionUpdateData,
  BrainFunctionAddData,
} from '@shared/models/brain-function-content';
import { PendingAssessment } from '@shared/models/pending-assessment';

@injectable()
export default class AssessmentsStore extends BaseStore<PendingAssessment, AssessmentsService> {
  static diToken = Symbol('assessments-store');
  private _content: { number: BrainFunctionContent[] } = { number: [] };

  constructor() {
    super({ service: container.get<AssessmentsService>(AssessmentsService.diToken) });
    makeObservable<any>(this, {
      _content: observable,
      getPendingList: action,
      getMatrix: action,
      getContent: action,
    });

    this.loading = {
      list: true,
      item: false,
    };
  }

  get content(): { number: BrainFunctionContent[] } {
    return this._content;
  }

  getPendingList = async (params?: GetPendingAssessmentsParams) => {
    runInAction(() => {
      this.loading.list = true;
    });

    try {
      const { items, pageInfo } = await this.service.getPendingList(params);

      runInAction(() => {
        this._list = items;
        this._pageInfo = pageInfo;
      });

      return {
        items,
        pageInfo,
      };
    } finally {
      runInAction(() => {
        this.loading.list = false;
      });
    }
  };

  getMatrix = async () => {
    return await this.service.getMatrix();
  };

  getContent = async (brainFunctionId: number) => {
    const brainFunctionContent = await this.service.getContent(brainFunctionId);

    runInAction(() => {
      this._content = {
        ...this._content,
        [brainFunctionId]: brainFunctionContent,
      };
    });

    return brainFunctionContent;
  };

  updateContent = async (brainFunctionId: number, data: BrainFunctionUpdateData) => {
    return await this.service.updateContent(brainFunctionId, data);
  };

  deleteContent = async (brainFunctionId: number, data: BrainFunctionDeleteData) => {
    return await this.service.deleteContent(brainFunctionId, data);
  };

  addContent = (brainFunctionId: number, data: BrainFunctionAddData) => {
    return this.service.addContent(brainFunctionId, data);
  };

  updateTreatmentPlanModel = async (formData: FormData, setProgress: (value: number) => void) => {
    return this.service.updateTreatmentPlanModel(formData, setProgress);
  };

  cancelUpload = () => {
    return this.service.cancelUpload();
  };

  getAssessmentList = pagination => {
    return this.service.getAssessmentList(pagination);
  };
}
